import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for responsive design with media queries
 * @param query - CSS media query string
 */
export function useMediaQuery(query: string): boolean {
    const getMatches = useCallback((mediaQuery: string): boolean => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.matchMedia(mediaQuery).matches;
    }, []);

    const [matches, setMatches] = useState<boolean>(() => getMatches(query));

    useEffect(() => {
        const mediaQueryList = window.matchMedia(query);

        const handleChange = (event: MediaQueryListEvent): void => {
            setMatches(event.matches);
        };

        // Set initial value
        setMatches(mediaQueryList.matches);

        // Add listener
        mediaQueryList.addEventListener('change', handleChange);

        return () => {
            mediaQueryList.removeEventListener('change', handleChange);
        };
    }, [query]);

    return matches;
}

/**
 * Predefined breakpoint hooks following Tailwind CSS defaults
 */
export function useIsMobile(): boolean {
    return !useMediaQuery('(min-width: 640px)');
}

export function useIsTablet(): boolean {
    return useMediaQuery('(min-width: 640px) and (max-width: 1023px)');
}

export function useIsDesktop(): boolean {
    return useMediaQuery('(min-width: 1024px)');
}

export function usePrefersDarkMode(): boolean {
    return useMediaQuery('(prefers-color-scheme: dark)');
}

export function usePrefersReducedMotion(): boolean {
    return useMediaQuery('(prefers-reduced-motion: reduce)');
}

